import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Container,
  IconButton,
  Collapse,
  Tooltip,
  Avatar,
  Badge,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import InventoryIcon from '@mui/icons-material/Inventory';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { FlagUS, FlagUK, FlagJP, FlagCA } from './FlagIcons';
import axios from 'axios';
import * as XLSX from 'xlsx';
import GoldIngotLoader from './GoldIngotLoader';

// Market configurations
const MARKETS = {
  US: { code: 'US', name: 'United States', domain: 'amazon.com', flag: '🇺🇸' },
  UK: { code: 'UK', name: 'United Kingdom', domain: 'amazon.co.uk', flag: '🇬🇧' },
  JP: { code: 'JP', name: 'Japan', domain: 'amazon.co.jp', flag: '🇯🇵' },
  CA: { code: 'CA', name: 'Canada', domain: 'amazon.ca', flag: '🇨🇦' },
};

// Get color based on search frequency rank
const getRankColor = (rank) => {
  if (rank <= 100) return '#4caf50';
  if (rank <= 1000) return '#2196f3';
  if (rank <= 10000) return '#ff9800';
  return '#9e9e9e';
};

// Get position chip with medal colors
const getPositionChip = (position) => {
  const colors = {
    1: { bg: '#ffd700', text: '#000' },
    2: { bg: '#c0c0c0', text: '#000' },
    3: { bg: '#cd7f32', text: '#fff' },
  };
  const color = colors[position] || { bg: '#e0e0e0', text: '#000' };
  return (
    <Chip
      label={`#${position}`}
      size="small"
      sx={{
        backgroundColor: color.bg,
        color: color.text,
        fontWeight: 'bold',
        fontSize: '0.7rem',
        height: 20,
        minWidth: 32,
      }}
    />
  );
};

// Format sales number
const formatSales = (sales) => {
  if (!sales && sales !== 0) return '-';
  if (sales >= 1000) {
    return `${(sales / 1000).toFixed(1)}K`;
  }
  return sales.toLocaleString();
};

// Product Card Component with Keywords Table - Each ASIN clearly separated
const ProductKeywordsCard = ({ product, marketCode = 'US', expanded, onToggle, index }) => {
  const domain = MARKETS[marketCode]?.domain || 'amazon.com';

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: '2px solid #e0e0e0',
        '&:hover': { borderColor: '#ff9800' },
      }}
    >
      {/* ASIN Header - Distinct colored bar */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          backgroundColor: '#fff8e1',
          borderBottom: '1px solid #ffe0b2',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#ffecb3' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Sequence Number */}
          <Chip
            label={index + 1}
            size="small"
            sx={{
              backgroundColor: '#ff9800',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.8rem',
              minWidth: 28,
              height: 28,
            }}
          />

          {/* Product Image */}
          <Avatar
            src={product.imageUrl || product.image}
            variant="rounded"
            sx={{ width: 56, height: 56, border: '2px solid #fff', boxShadow: 1 }}
          >
            {product.title?.charAt(0) || 'P'}
          </Avatar>

          {/* Product Info */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {product.rank && (
                <Chip
                  label={`Rank #${product.rank}`}
                  size="small"
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: 22,
                  }}
                />
              )}
              <Chip
                label={product.asin}
                size="small"
                component="a"
                href={`https://${domain}/dp/${product.asin}`}
                target="_blank"
                clickable
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  height: 22,
                  backgroundColor: '#e65100',
                  color: 'white',
                  '&:hover': { backgroundColor: '#bf360c' },
                }}
                onClick={(e) => e.stopPropagation()}
              />
              {product.monthly_sales > 0 && (
                <Chip
                  label={`${formatSales(product.monthly_sales)}/mo`}
                  size="small"
                  sx={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    height: 22,
                  }}
                />
              )}
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                maxWidth: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {product.title || product.product_name || 'Unknown Product'}
            </Typography>
          </Box>
        </Box>

        {/* Keywords Count & Expand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<SearchIcon sx={{ fontSize: 16 }} />}
            label={`${product.keywords?.length || 0} keywords`}
            size="medium"
            sx={{
              backgroundColor: product.keywords?.length > 0 ? '#e8f5e9' : '#f5f5f5',
              color: product.keywords?.length > 0 ? '#2e7d32' : '#666',
              fontWeight: 'bold',
              fontSize: '0.8rem',
            }}
          />
          <IconButton size="small" sx={{ backgroundColor: 'white' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Keywords Table */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
          {product.keywords && product.keywords.length > 0 ? (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5 }}>Search Term</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, width: 120 }}>Search Rank</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, width: 100 }}>Position</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, width: 100 }}>Click %</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, width: 100 }}>Conv %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {product.keywords
                    .sort((a, b) => (a.search_frequency_rank || 999999) - (b.search_frequency_rank || 999999))
                    .slice(0, 3)
                    .map((kw, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        '&:hover': { backgroundColor: '#fff8e1' },
                        '&:nth-of-type(odd)': { backgroundColor: 'white' },
                        '&:nth-of-type(even)': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SearchIcon sx={{ fontSize: 16, color: '#bbb' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {kw.search_term}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Chip
                          label={`#${kw.search_frequency_rank?.toLocaleString() || '-'}`}
                          size="small"
                          sx={{
                            backgroundColor: getRankColor(kw.search_frequency_rank),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            height: 24,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {getPositionChip(kw.product_position || 1)}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#1976d2', fontWeight: 600 }}>
                          {parseFloat(kw.click_share || 0).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#388e3c', fontWeight: 600 }}>
                          {parseFloat(kw.conversion_share || 0).toFixed(1)}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" icon={<SearchIcon />}>
              No keyword data available for this ASIN
            </Alert>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Category Section Component
const CategorySection = ({ category, marketCode, expandedProducts, onToggleProduct, combinedView }) => {
  const [expanded, setExpanded] = useState(true);
  const domain = MARKETS[marketCode]?.domain || 'amazon.com';

  const totalKeywords = category.products?.reduce((sum, p) => sum + (p.keywords?.length || 0), 0) || 0;

  // For combined view: take top 3 keywords from each product, then combine all together
  const combinedKeywords = useMemo(() => {
    if (!combinedView) return [];
    const allKeywords = [];
    category.products?.forEach(product => {
      // Get top 3 keywords for this product
      const top3Keywords = (product.keywords || [])
        .sort((a, b) => (a.search_frequency_rank || 999999) - (b.search_frequency_rank || 999999))
        .slice(0, 3);

      top3Keywords.forEach(kw => {
        allKeywords.push({
          ...kw,
          asin: product.asin,
          productTitle: product.title || product.product_name || 'Unknown',
          productImage: product.imageUrl || product.image,
          productRank: product.rank || product.hot_rank,
        });
      });
    });
    // Sort all combined keywords by search frequency rank
    return allKeywords.sort((a, b) => (a.search_frequency_rank || 999999) - (b.search_frequency_rank || 999999));
  }, [category.products, combinedView]);

  return (
    <Paper
      sx={{
        mb: 3,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {/* Category Header */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: '#1976d2',
          color: 'white',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#1565c0' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FolderIcon sx={{ fontSize: 28 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`#${category.priority || category.sequence || '-'}`}
                size="small"
                sx={{
                  backgroundColor: '#FF6A00',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  height: 20,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                {category.name}
              </Typography>
            </Box>
            {category.category_type && (
              <Typography variant="body2" sx={{ opacity: 0.85, fontSize: '0.75rem' }}>
                {category.category_type}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge badgeContent={category.products?.length || 0} color="warning">
            <Chip
              icon={<InventoryIcon sx={{ fontSize: 16 }} />}
              label="Products"
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Badge>
          <Chip
            icon={<SearchIcon sx={{ fontSize: 16 }} />}
            label={`${totalKeywords} keywords`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
          <IconButton size="small" sx={{ color: 'white' }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Products List or Combined View */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          {combinedView ? (
            // Combined View: Top 3 keywords across all products
            combinedKeywords.length > 0 ? (
              <TableContainer component={Paper} elevation={2}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#9c27b0' }}>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, color: 'white' }}>Search Term</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, color: 'white', width: 120 }}>Search Rank</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, color: 'white', width: 300 }}>Product (ASIN)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, color: 'white', width: 100 }}>Position</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, color: 'white', width: 100 }}>Click %</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem', py: 1.5, color: 'white', width: 100 }}>Conv %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {combinedKeywords.map((kw, idx) => (
                      <TableRow
                        key={`${kw.asin}-${kw.search_term}-${idx}`}
                        sx={{
                          '&:hover': { backgroundColor: '#f3e5f5' },
                          backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa',
                        }}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={idx + 1}
                              size="small"
                              sx={{
                                backgroundColor: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : '#cd7f32',
                                color: idx === 0 ? '#000' : idx === 1 ? '#000' : '#fff',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                height: 24,
                                minWidth: 24,
                              }}
                            />
                            <SearchIcon sx={{ fontSize: 16, color: '#bbb' }} />
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {kw.search_term}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Chip
                            label={`#${kw.search_frequency_rank?.toLocaleString() || '-'}`}
                            size="small"
                            sx={{
                              backgroundColor: getRankColor(kw.search_frequency_rank),
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              src={kw.productImage}
                              variant="rounded"
                              sx={{ width: 32, height: 32 }}
                            >
                              {kw.productTitle?.charAt(0) || 'P'}
                            </Avatar>
                            <Box>
                              <Chip
                                label={kw.asin}
                                size="small"
                                component="a"
                                href={`https://${domain}/dp/${kw.asin}`}
                                target="_blank"
                                clickable
                                sx={{
                                  fontFamily: 'monospace',
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  height: 20,
                                  backgroundColor: '#e65100',
                                  color: 'white',
                                  '&:hover': { backgroundColor: '#bf360c' },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: '#666',
                                }}
                              >
                                {kw.productTitle}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          {getPositionChip(kw.product_position || 1)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#1976d2', fontWeight: 600 }}>
                            {parseFloat(kw.click_share || 0).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#388e3c', fontWeight: 600 }}>
                            {parseFloat(kw.conversion_share || 0).toFixed(1)}%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No keyword data available for this category</Alert>
            )
          ) : (
            // Normal View: Products listed separately
            category.products && category.products.length > 0 ? (
              category.products.map((product, index) => (
                <ProductKeywordsCard
                  key={product.asin}
                  product={product}
                  marketCode={marketCode}
                  expanded={expandedProducts[product.asin] || false}
                  onToggle={() => onToggleProduct(product.asin)}
                  index={index}
                />
              ))
            ) : (
              <Alert severity="info">No products with keyword data in this category</Alert>
            )
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Main Page Component
const KeywordRankingReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoriesData, setCategoriesData] = useState([]);
  const [keywordData, setKeywordData] = useState({});
  const [latestWeek, setLatestWeek] = useState(null);
  const [selectedMarketTab, setSelectedMarketTab] = useState('US');
  const [expandedProducts, setExpandedProducts] = useState({});
  const [combinedView, setCombinedView] = useState(false);

  // Fetch categories from scraper_categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/scraper-config/categories');
      if (response.data.code === 'OK') {
        return response.data.data;
      }
      return [];
    } catch (err) {
      console.error('Error fetching categories:', err);
      return [];
    }
  };

  // Fetch keyword rankings for all ASINs
  const fetchKeywordRankings = async (marketCode) => {
    try {
      const response = await axios.get('/api/search-terms/all-rankings', {
        params: { market: marketCode, limit: 20, pageSize: 1000 },
      });
      if (response.data.success) {
        return response.data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching keyword rankings:', err);
      return null;
    }
  };

  // Fetch products for each category
  const fetchCategoryProducts = async (marketCode) => {
    try {
      const response = await axios.get(`/api/products?marketCode=${marketCode}`);
      if (response.data.success) {
        return response.data.categories;
      }
      return [];
    } catch (err) {
      console.error('Error fetching products:', err);
      return [];
    }
  };

  // Load all data
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get categories config
      const categories = await fetchCategories();

      // Get products and keyword data for the selected market
      const marketToFetch = selectedMarketTab === 'ALL' ? 'US' : selectedMarketTab;
      const [productCategories, keywordRankings] = await Promise.all([
        fetchCategoryProducts(marketToFetch),
        fetchKeywordRankings(marketToFetch),
      ]);

      // Build keyword lookup by ASIN
      const keywordsByAsin = {};
      if (keywordRankings?.rankings) {
        Object.entries(keywordRankings.rankings).forEach(([asin, data]) => {
          keywordsByAsin[asin] = data.terms || [];
        });
      }

      // Merge categories with products and keywords
      const mergedCategories = categories
        .filter(cat => selectedMarketTab === 'ALL' || (cat.market_code || 'US') === selectedMarketTab)
        .sort((a, b) => a.priority - b.priority)
        .map((cat, idx) => {
          // Find matching product category
          const productCat = productCategories.find(pc =>
            pc.categoryName === cat.name ||
            pc.categoryId === cat.id
          );

          // Get products with keywords
          const products = (productCat?.products || [])
            .filter(p => p.hot_rank || p.rank) // Only products with rankings
            .sort((a, b) => (a.rank || a.hot_rank || 999) - (b.rank || b.hot_rank || 999))
            .slice(0, 20) // Limit to top 20 per category
            .map(p => ({
              ...p,
              keywords: keywordsByAsin[p.asin] || [],
            }));

          return {
            ...cat,
            sequence: idx + 1,
            products,
          };
        })
        .filter(cat => cat.products.length > 0 || cat.is_enabled); // Show categories with products or enabled

      setCategoriesData(mergedCategories);
      setLatestWeek(keywordRankings?.latestWeek);

      // Auto-expand first product in first 3 categories
      const initialExpanded = {};
      mergedCategories.slice(0, 3).forEach(cat => {
        if (cat.products?.[0]) {
          initialExpanded[cat.products[0].asin] = true;
        }
      });
      setExpandedProducts(initialExpanded);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarketTab]);

  // Filter categories by market
  const filteredCategories = useMemo(() => {
    if (selectedMarketTab === 'ALL') {
      return categoriesData;
    }
    return categoriesData.filter(cat => (cat.market_code || 'US') === selectedMarketTab);
  }, [categoriesData, selectedMarketTab]);

  // Market counts
  const marketCounts = useMemo(() => ({
    ALL: categoriesData.length,
    US: categoriesData.filter(c => (c.market_code || 'US') === 'US').length,
    UK: categoriesData.filter(c => c.market_code === 'UK').length,
    JP: categoriesData.filter(c => c.market_code === 'JP').length,
    CA: categoriesData.filter(c => c.market_code === 'CA').length,
  }), [categoriesData]);

  const toggleProduct = (asin) => {
    setExpandedProducts(prev => ({
      ...prev,
      [asin]: !prev[asin],
    }));
  };

  const expandAll = () => {
    const expanded = {};
    filteredCategories.forEach(cat => {
      cat.products?.forEach(p => {
        expanded[p.asin] = true;
      });
    });
    setExpandedProducts(expanded);
  };

  const collapseAll = () => {
    setExpandedProducts({});
  };

  // Export to Excel
  const exportToExcel = () => {
    // Prepare data for Excel
    const excelData = [];

    filteredCategories.forEach(category => {
      category.products?.forEach(product => {
        if (product.keywords && product.keywords.length > 0) {
          product.keywords.forEach(kw => {
            excelData.push({
              'Category': category.name,
              'Category Type': category.category_type || '',
              'Priority': category.priority || '',
              'ASIN': product.asin,
              'Product Title': product.title || product.product_name || '',
              'Product Rank': product.rank || '',
              'Monthly Sales': product.monthly_sales || '',
              'Search Term': kw.search_term,
              'Search Frequency Rank': kw.search_frequency_rank,
              'Product Position': kw.product_position || 1,
              'Click Share (%)': parseFloat(kw.click_share || 0).toFixed(2),
              'Conversion Share (%)': parseFloat(kw.conversion_share || 0).toFixed(2),
            });
          });
        } else {
          // Product without keywords
          excelData.push({
            'Category': category.name,
            'Category Type': category.category_type || '',
            'Priority': category.priority || '',
            'ASIN': product.asin,
            'Product Title': product.title || product.product_name || '',
            'Product Rank': product.rank || '',
            'Monthly Sales': product.monthly_sales || '',
            'Search Term': '',
            'Search Frequency Rank': '',
            'Product Position': '',
            'Click Share (%)': '',
            'Conversion Share (%)': '',
          });
        }
      });
    });

    if (excelData.length === 0) {
      alert('No data to export');
      return;
    }

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Category
      { wch: 15 }, // Category Type
      { wch: 8 },  // Priority
      { wch: 12 }, // ASIN
      { wch: 50 }, // Product Title
      { wch: 10 }, // Product Rank
      { wch: 12 }, // Monthly Sales
      { wch: 30 }, // Search Term
      { wch: 18 }, // Search Frequency Rank
      { wch: 15 }, // Product Position
      { wch: 15 }, // Click Share
      { wch: 18 }, // Conversion Share
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Keyword Rankings');

    // Generate filename with date and market
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `keyword_ranking_report_${selectedMarketTab}_${dateStr}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  };

  // Calculate totals
  const totalProducts = filteredCategories.reduce((sum, cat) => sum + (cat.products?.length || 0), 0);
  const totalKeywords = filteredCategories.reduce((sum, cat) =>
    sum + (cat.products?.reduce((pSum, p) => pSum + (p.keywords?.length || 0), 0) || 0), 0);

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Header Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingUpIcon sx={{ fontSize: 32, color: '#ff9800' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Keyword Ranking Report
            </Typography>
            {latestWeek && (
              <Chip
                label={`Week: ${latestWeek}`}
                size="small"
                sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Combined View Toggle */}
            <Button
              variant={combinedView ? 'contained' : 'outlined'}
              startIcon={combinedView ? <ViewModuleIcon /> : <ViewListIcon />}
              onClick={() => setCombinedView(!combinedView)}
              size="small"
              sx={{
                backgroundColor: combinedView ? '#9c27b0' : 'transparent',
                borderColor: '#9c27b0',
                color: combinedView ? 'white' : '#9c27b0',
                '&:hover': {
                  backgroundColor: combinedView ? '#7b1fa2' : 'rgba(156, 39, 176, 0.1)',
                  borderColor: '#9c27b0',
                },
              }}
            >
              {combinedView ? 'Combined' : 'By Product'}
            </Button>

            {/* Expand/Collapse All */}
            <Button
              variant="outlined"
              startIcon={<UnfoldMoreIcon />}
              onClick={expandAll}
              size="small"
              sx={{ borderColor: '#1976d2', color: '#1976d2' }}
            >
              Expand All
            </Button>
            <Button
              variant="outlined"
              startIcon={<UnfoldLessIcon />}
              onClick={collapseAll}
              size="small"
              sx={{ borderColor: '#757575', color: '#757575' }}
            >
              Collapse
            </Button>

            {/* Excel Export */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={loading || filteredCategories.length === 0}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#388e3c' },
              }}
            >
              Excel
            </Button>

            {/* Refresh */}
            <Tooltip title="Refresh">
              <IconButton onClick={loadData} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Market Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedMarketTab}
            onChange={(e, newValue) => setSelectedMarketTab(newValue)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              value="ALL"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>All Markets</span>
                  <Chip label={loading ? '-' : marketCounts.ALL} size="small" />
                </Box>
              }
            />
            <Tab
              value="US"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagUS size={20} />
                  <span>US</span>
                  <Chip label={loading ? '-' : marketCounts.US} size="small" color={!loading && marketCounts.US > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
            <Tab
              value="UK"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagUK size={20} />
                  <span>UK</span>
                  <Chip label={loading ? '-' : marketCounts.UK} size="small" color={!loading && marketCounts.UK > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
            <Tab
              value="JP"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagJP size={20} />
                  <span>JP</span>
                  <Chip label={loading ? '-' : marketCounts.JP} size="small" color={!loading && marketCounts.JP > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
            <Tab
              value="CA"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlagCA size={20} />
                  <span>CA</span>
                  <Chip label={loading ? '-' : marketCounts.CA} size="small" color={!loading && marketCounts.CA > 0 ? 'primary' : 'default'} />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Summary Statistics */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
            <Typography variant="h5" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
              {loading ? '-' : filteredCategories.length}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              Categories
            </Typography>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center', backgroundColor: '#fff3e0' }}>
            <Typography variant="h5" sx={{ color: '#e65100', fontWeight: 'bold' }}>
              {loading ? '-' : totalProducts}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              Products
            </Typography>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
            <Typography variant="h5" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
              {loading ? '-' : totalKeywords}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              Total Keywords
            </Typography>
          </Paper>
          <Paper sx={{ p: 1.5, flex: 1, textAlign: 'center', backgroundColor: '#fce4ec' }}>
            <Typography variant="h5" sx={{ color: '#c2185b', fontWeight: 'bold' }}>
              {MARKETS[selectedMarketTab]?.flag || '🌐'} {selectedMarketTab}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
              Market
            </Typography>
          </Paper>
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && <GoldIngotLoader minHeight={300} />}

      {/* Category Sections */}
      {!loading && filteredCategories.length > 0 && filteredCategories.map((category) => (
        <CategorySection
          key={category.id}
          category={category}
          marketCode={selectedMarketTab === 'ALL' ? (category.market_code || 'US') : selectedMarketTab}
          expandedProducts={expandedProducts}
          onToggleProduct={toggleProduct}
          combinedView={combinedView}
        />
      ))}

      {/* Empty State */}
      {!loading && filteredCategories.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No categories found for {selectedMarketTab === 'ALL' ? 'any market' : selectedMarketTab}.
          Configure categories in the Scraper Config page.
        </Alert>
      )}
    </Container>
  );
};

export default KeywordRankingReport;
